import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  readonly message: string;
  readonly isVisible: boolean;
  readonly onHide: () => void;
  /** Auto-dismiss after this many ms. Defaults to 3000 (3s). */
  readonly duration?: number;
  readonly type?: ToastType;
}

/**
 * Top-of-screen toast. Auto-dismisses after `duration` ms (default 3000).
 * Mirrors HTHD's `customer-app/src/components/ui/Toast.tsx` — keeps the same
 * `message` / `isVisible` / `onHide` / `type` prop names so callers can
 * lift their HTHD code unchanged.
 */
export function Toast({
  message,
  isVisible,
  onHide,
  duration = 3000,
  type = 'success',
}: ToastProps): JSX.Element | null {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setShow(false);
      return;
    }
    const showFrame = requestAnimationFrame(() => setShow(true));
    const hideTimer = setTimeout(() => {
      setShow(false);
      const finalize = setTimeout(onHide, 250);
      return () => clearTimeout(finalize);
    }, duration);
    return () => {
      cancelAnimationFrame(showFrame);
      clearTimeout(hideTimer);
    };
  }, [isVisible, duration, onHide]);

  if (!isVisible) return null;
  if (typeof document === 'undefined') return null;

  const cls = `ferro-toast ferro-toast--${type} ${show ? 'ferro-toast--visible' : ''}`.trim();

  return createPortal(
    <div className={cls} role="status" aria-live="polite">
      <span className="ferro-toast__icon" aria-hidden="true">
        <ToastIcon type={type} />
      </span>
      <span className="ferro-toast__message">{message}</span>
    </div>,
    document.body,
  );
}

function ToastIcon({ type }: { readonly type: ToastType }): JSX.Element {
  switch (type) {
    case 'success':
      return (
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'error':
      return (
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'warning':
      return (
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
}
